import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, Calendar, Plus, Sparkles, Trash2 } from 'lucide-react';
import { CoreOption } from './types';

interface CoreOptionsCardProps {
  coreOptions: CoreOption[];
  setCoreOptions: (options: CoreOption[]) => void;
  currency: string;
  onAddPricing: (optionId: string, isCore: boolean, optionType: string) => void;
  onRemovePricing: (optionId: string, isCore: boolean, pricingIndex: number) => void;
  renderPricingPreview: (pricing: any) => string | null;
}

export function CoreOptionsCard({
  coreOptions,
  setCoreOptions,
  currency,
  onAddPricing,
  onRemovePricing,
  renderPricingPreview
}: CoreOptionsCardProps) {
  const updateCoreOption = (id: string, updates: Partial<CoreOption>) => {
    setCoreOptions(coreOptions.map(opt => 
      opt.id === id ? { ...opt, ...updates } : opt
    ));
  };

  return (
    <Card className="border border-slate-200 shadow-lg bg-white">
      <CardHeader className="bg-slate-50 border-b border-slate-200">
        <CardTitle className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-100 border border-emerald-200 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-emerald-600" />
          </div>
          Core Options
          <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">
            Required
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        {coreOptions.map((option) => (
          <div key={option.id} className="space-y-4">
            <div className="flex items-center gap-3">
              {option.id === 'people' ? (
                <Users className="w-5 h-5 text-blue-600" />
              ) : (
                <Calendar className="w-5 h-5 text-emerald-600" />
              )}
              <div className="flex-1">
                <Label className="text-base font-medium">{option.label}</Label>
                <p className="text-sm text-slate-600 mt-1">
                  {option.id === 'people' 
                    ? 'Base group size for pricing calculations'
                    : 'Duration affects per-day pricing'
                  }
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ml-8">
              <div>
                <Label htmlFor={`${option.id}-min`} className="text-sm">Minimum</Label>
                <Input
                  id={`${option.id}-min`}
                  type="number"
                  value={option.min || 1}
                  onChange={(e) => updateCoreOption(option.id, { min: parseInt(e.target.value) || 1 })}
                  className="focus:ring-primary focus:border-primary shadow-sm"
                  min="1"
                />
              </div>
              <div>
                <Label htmlFor={`${option.id}-max`} className="text-sm">Maximum</Label>
                <Input
                  id={`${option.id}-max`}
                  type="number"
                  value={option.max || 20}
                  onChange={(e) => updateCoreOption(option.id, { max: parseInt(e.target.value) || 20 })}
                  className="focus:ring-primary focus:border-primary shadow-sm"
                  min="1"
                />
              </div>
              <div>
                <Label htmlFor={`${option.id}-default`} className="text-sm">Default Value</Label>
                <Input
                  id={`${option.id}-default`}
                  type="number"
                  value={option.defaultValue || 1}
                  onChange={(e) => updateCoreOption(option.id, { defaultValue: parseInt(e.target.value) || 1 })}
                  className="focus:ring-primary focus:border-primary shadow-sm"
                  min={option.min || 1}
                  max={option.max || 20}
                />
              </div>
            </div>

            {/* Pricing Section */}
            <div className="ml-8 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-slate-700">Additional Pricing</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onAddPricing(option.id, true, 'number')}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Pricing
                </Button>
              </div>

              {option.pricing && option.pricing.length > 0 && (
                <div className="space-y-2">
                  {option.pricing.map((pricing, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <div className="text-sm font-medium text-slate-900">
                          {renderPricingPreview(pricing)}
                        </div>
                        {pricing.description && (
                          <div className="text-xs text-slate-600">
                            {pricing.description}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemovePricing(option.id, true, idx)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
} 